import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FormInputs from "../../Components/Shared/FormInputs/FormInputs";
import DropDown from "../../Components/Shared/Dropdown/Dropdown";
import ErrorImg from "../../Assets/Images/CommanImages/Error.png";
import InterogativeImg from "../../Assets/Images/CommanImages/Interogative.png";
import { FULL_API_BASE_URL } from "../../Config/Config";
import { fetchCourseData } from "../../Redux/StudentRegistration/courseSlice";
import { fetchSessionData } from "../../Redux/Sessions/sessionSlice";
import MultiSeledDropdown from "../CreateQuestionsPaper/MultiDropdown/MultiSeledDropdown";
import SingleSelectSearchDropdown from "./SingleSelectSearchDropdown";
import DropdownWithPagination from "./DropdownWithPagination";
import TableWithCrossStudentGroup from "./TableWithCrossStudentGroup";
import "./studentgroup.css";
import SelectDropdownValues from "../../Components/Shared/SelectedValuesDropdown/SelectDropdown";
import SuccessImg from "./../../Assets/Images/CommanImages/Informative.png";

function StudentGroupEdit({
  dataClient,
  setMessageBox,
  messageBox,
  setIsMessageBoxVisible,
  setActiveImg,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { coursesdata } = useSelector((state) => state.course);
  const { sessionsData } = useSelector((state) => state.sessions);

  // ----------- state ----------
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [studentGroupName, setStudentGroupName] = useState("");
  const [studentGroupDesc, setStudentGroupDesc] = useState("");
  const [comments, setComments] = useState("");

  const [facultyOptions, setFacultyOptions] = useState([]);
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);

  const userName = dataClient?.client_schema_name?.[6]?.[0]?.user_name || null;
  const clientBranchId =
    dataClient?.client_schema_name?.[6]?.[0]?.client_branch_id ?? null;
  const clientId = dataClient?.client_schema_name?.[6]?.[0]?.client_id || null;

  // unify token getter
  const accessToken =
    localStorage.getItem("other") ?? localStorage.getItem("others") ?? "";

  const [isOpenShareBranch, setIsOpenShareBranch] = useState(true);
  const toggleAccordionShareBranch = () =>
    setIsOpenShareBranch(!isOpenShareBranch);

  // students dropdown options (each option includes fullData where possible)
  const [studentOptions, setStudentOptions] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const isUpdatingFromTable = useRef(false);

  const studentGroupId = new URLSearchParams(window.location.search).get(
    "MNTStudentGroupID"
  );

  const [studentGroupData, setStudentGroupData] = useState(null);

  const [selectedBranches, setSelectedBranches] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);

  // debounce refs
  const studentSearchTimeout = useRef(null);
  const facultySearchTimeout = useRef(null);
  const studentSearchAbort = useRef(null);
  const facultySearchAbort = useRef(null);

  // Edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ---------- status tracking ----------
  const [currentStatus, setCurrentStatus] = useState(
    studentGroupData?.status || ""
  );
  useEffect(() => {
    if (studentGroupData && studentGroupData.status !== undefined) {
      setCurrentStatus(studentGroupData.status);
      if (!isCurrentlyActive(studentGroupData.status)) {
        setIsEditMode(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentGroupData]);

  function isCurrentlyActive(status) {
    if (status === null || status === undefined) return false;
    const s = String(status).toLowerCase();
    return s === "active" || s === "true" || s === "1";
  }

  const [selectedCoordinatorOption, setSelectedCoordinatorOption] =
    useState(null);

  useEffect(() => {
    const groupId = new URLSearchParams(window.location.search).get(
      "MNTStudentGroupID"
    );
    if (!groupId) return;

    const fetchStudentGroup = async () => {
      try {
        const token = accessToken;
        const res = await fetch(
          `${FULL_API_BASE_URL}/get-student-grp-details-by-id?student_group_id=${groupId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await res.json();
        if (data.status === 200 && data.data) {
          const payload = data.data;

          setStudentGroupData(payload);
          setStudentGroupName(payload.student_group_name || "");
          setStudentGroupDesc(payload.student_group_desc || "");
          setComments(payload.comments || "");
          setSelectedCourse(payload.course_id || null);
          setSelectedSession(payload.session_id || null);

          setSelectedSubjects(
            Array.isArray(payload.subjects)
              ? payload.subjects.map((s) => Number(s.subject_id))
              : []
          );

          const selectedStudentIds = Array.isArray(payload.students)
            ? payload.students.map((s) => Number(s.student_id))
            : [];
          setSelectedStudents(selectedStudentIds);

          const selectedBranchIds = Array.isArray(payload.shared_branches)
            ? payload.shared_branches.map((b) => Number(b.branch_id))
            : [];
          setSelectedBranches(selectedBranchIds);

          // FIXED: Set coordinator properly
          const coordId = payload.student_group_coord_emp_id;
          const coordName = payload.coordinator_name;
          setSelectedCoordinator(coordId || null);

          // Set the coordinator option object for the dropdown
          if (coordId && coordName) {
            setSelectedCoordinatorOption({
              id: coordId,
              label: coordName,
            });
          }

          const prefillStudentOptions = (payload.students || []).map((s) => ({
            label: `${s.student_name || s.student_full_name || "---"} (${
              s.roll_number || ""
            }) - ${s.branch_name || ""}`,
            id: Number(s.student_id),
            fullData: {
              student_full_name: s.student_name || s.student_full_name,
              roll_number: s.roll_number,
              course_name: s.course_name || "",
              batch_name: s.batch_name || "",
              branch_name: s.branch_name || "",
              class_type: s.class_type || "",
              status: s.status || "",
            },
          }));

          setStudentOptions((prev) => {
            const byId = new Map(prev.map((p) => [Number(p.id), p]));
            prefillStudentOptions.forEach((p) => byId.set(Number(p.id), p));
            return Array.from(byId.values());
          });

          const prefillBranchOptions = (payload.shared_branches || []).map(
            (b) => ({
              label: b.branch_name || `Branch ${b.branch_id}`,
              id: Number(b.branch_id),
            })
          );
          setBranchOptions((prev) => {
            const map = new Map((prev || []).map((p) => [Number(p.id), p]));
            prefillBranchOptions.forEach((opt) => map.set(Number(opt.id), opt));
            return Array.from(map.values());
          });

          setCurrentStatus(payload.status);
          if (!isCurrentlyActive(payload.status)) setIsEditMode(false);
        }
      } catch (error) {
        console.error("Error fetching student group:", error);
      }
    };

    fetchStudentGroup();
  }, []);

  const handleCancelEdit = () => {
    if (studentGroupData) {
      setStudentGroupName(studentGroupData.student_group_name || "");
      setStudentGroupDesc(studentGroupData.student_group_desc || "");
      setComments(studentGroupData.comments || "");
      setSelectedCourse(studentGroupData.course_id || null);
      setSelectedSession(studentGroupData.session_id || null);
      setSelectedSubjects(
        Array.isArray(studentGroupData.subjects)
          ? studentGroupData.subjects.map((s) => s.subject_id)
          : []
      );
      setSelectedStudents(
        Array.isArray(studentGroupData.students)
          ? studentGroupData.students.map((s) => s.student_id)
          : []
      );
      setSelectedBranches(
        Array.isArray(studentGroupData.shared_branches)
          ? studentGroupData.shared_branches.map((b) => b.branch_id)
          : []
      );

      // FIXED: Reset coordinator properly
      const coordId = studentGroupData.student_group_coord_emp_id;
      const coordName = studentGroupData.coordinator_name;
      setSelectedCoordinator(coordId || null);

      if (coordId && coordName) {
        setSelectedCoordinatorOption({
          id: coordId,
          label: coordName,
        });
      } else {
        setSelectedCoordinatorOption(null);
      }

      const prefillStudentOptions = (studentGroupData.students || []).map(
        (s) => ({
          label: `${s.student_full_name} (${s.roll_number}) - ${s.branch_name}`,
          id: s.student_id,
          fullData: s,
        })
      );
      setStudentOptions(prefillStudentOptions);
    }
    setIsEditMode(false);
  };

  // fetch courses & sessions
  useEffect(() => {
    dispatch(fetchCourseData());
    dispatch(fetchSessionData());
  }, [dispatch]);

  // when course changes fetch subjects
  useEffect(() => {
    if (selectedCourse) {
      fetch(
        `${FULL_API_BASE_URL}/get-subjects-by-course/?course_id=${selectedCourse}`
      )
        .then((res) => res.json())
        .then((data) => {
          const fetchedSubjects = Array.isArray(data) ? data : data.data ?? [];
          setSubjects(fetchedSubjects);
          if (!studentGroupData) {
            setSelectedSubjects(fetchedSubjects.map((s) => s.subject_id));
          }
        })
        .catch((err) => console.error("Error fetching subjects:", err));
    } else {
      setSubjects([]);
      setSelectedSubjects([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse]);

  useEffect(() => {
    // Try to obtain a clientId from props first, then from localStorage as a fallback
    const resolvedClientId =
      clientBranchId ||
      (() => {
        try {
          const stored = localStorage.getItem("client_branch_id"); // adjust key if you use a different one
          return stored ? Number(stored) : null;
        } catch (e) {
          return null;
        }
      })();

    if (!resolvedClientId) {
      // If there's no client id yet, do nothing — wait for prop to update.
      // This ensures the effect will run again once clientBranchId changes.
      console.debug(
        "fetchBranches: waiting for clientBranchId to be available"
      );
      return;
    }

    const fetchBranches = async () => {
      try {
        const token = localStorage.getItem("other");
        const res = await fetch(
          `${FULL_API_BASE_URL}/get-branch-list?client_id=${clientId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const json = await res.json();

        if (json && json.status === 200 && Array.isArray(json.data)) {
          // normalize options
          const opts = json.data.map((b) => ({
            label: `${b.branch_name} / ${b.city_name} / ${b.state_name}`,
            id: b.branch_id,
            disabled: Number(b.branch_id) === clientBranchId,
          }));
          setBranchOptions(opts);
        } else {
          console.warn("fetchBranches: unexpected response", json);
          setBranchOptions([]);
        }
      } catch (err) {
        console.error("Error fetching branches:", err);
        setBranchOptions([]);
      }
    };

    fetchBranches();
  }, [clientBranchId, dataClient]);

  // debounced faculty search
  const fetchFaculty = (searchKey) => {
    if (!searchKey || searchKey.trim().length < 2) {
      setFacultyOptions([]);
      return;
    }

    if (facultySearchTimeout.current)
      clearTimeout(facultySearchTimeout.current);
    if (facultySearchAbort.current) {
      facultySearchAbort.current.abort();
      facultySearchAbort.current = null;
    }

    facultySearchTimeout.current = setTimeout(async () => {
      try {
        facultySearchAbort.current = new AbortController();
        const res = await fetch(
          `${FULL_API_BASE_URL}/get-faculty-list-by-searchkey/?search_key=${encodeURIComponent(
            searchKey
          )}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            signal: facultySearchAbort.current.signal,
          }
        );
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.data ?? [];
        const options = (list || []).map((f) => ({
          label: `${f.emp_first_name || ""} ${f.emp_middle_name || ""} ${
            f.emp_last_name || ""
          }`.trim(),
          id: f.emp_id ?? f.emp_id ?? f.employee_id,
          manager_emp_id: f.manager_emp_id,
        }));
        setFacultyOptions(options);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching faculty:", err);
      }
    }, 300);
  };

  // debounced students search
  const fetchStudents = (searchKey) => {
    if (!searchKey || searchKey.trim().length < 2) {
      setStudentOptions((prev) =>
        prev.filter((o) => selectedStudents.includes(o.id))
      );
      return;
    }

    if (studentSearchTimeout.current)
      clearTimeout(studentSearchTimeout.current);
    if (studentSearchAbort.current) {
      studentSearchAbort.current.abort();
      studentSearchAbort.current = null;
    }

    studentSearchTimeout.current = setTimeout(async () => {
      try {
        studentSearchAbort.current = new AbortController();
        const params = new URLSearchParams();
        params.append("search_key", searchKey);
        if (selectedCourse) params.append("course_id", selectedCourse);
        if (selectedSession) params.append("session_id", selectedSession);
        if (clientBranchId) params.append("client_branch_id", clientBranchId);

        const res = await fetch(
          `${FULL_API_BASE_URL}/search-student-across-branches?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            signal: studentSearchAbort.current.signal,
          }
        );
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.data ?? [];
        const opts = (list || []).map((s) => ({
          label: `${s.student_full_name || ""} (${s.roll_number || ""}) - ${
            s.branch_name || ""
          }`,
          id: s.student_id,
          fullData: s,
        }));

        setStudentOptions((prev) => {
          const map = new Map(prev.map((p) => [p.id, p]));
          opts.forEach((o) => map.set(o.id, o));
          return Array.from(map.values());
        });
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error(err);
      }
    }, 300);
  };

  // table data
  const headers = [
    "Student ID",
    "Student Name",
    "Roll Number",
    "Course",
    "Batch",
    "Branch",
    "Class Type",
    "Status",
  ];

  const tableData = (Array.isArray(selectedStudents) ? selectedStudents : [])
    .map((studentId) => {
      const opt = studentOptions.find((o) => o.id === studentId);
      if (!opt || !opt.fullData) return null;
      const s = opt.fullData;
      return [
        studentId,
        s.student_full_name || "---",
        s.roll_number || "---",
        s.course_name || "---",
        s.batch_name || "---",
        s.branch_name || "---",
        s.class_type || "---",
        s.status || 0,
      ];
    })
    .filter(Boolean);

  const handleTableRowSelection = (newSelectedRows) => {
    isUpdatingFromTable.current = true;
    setSelectedStudents(newSelectedRows);
    setTimeout(() => (isUpdatingFromTable.current = false), 0);
  };

  const handleDeleteRow = (rowId) => {
    setStudentOptions((prev) => prev.filter((o) => o.id !== rowId));
    setSelectedStudents((prev) => prev.filter((id) => id !== rowId));
  };

  // Edit handlers
  const handleEditClick = () => {
    if (!isCurrentlyActive(currentStatus)) return;
    setIsEditMode(true);
  };

  const handleSaveClick = async () => {
    if (
      !studentGroupName.trim() ||
      !studentGroupDesc.trim() ||
      !selectedCoordinator ||
      !selectedSession ||
      !selectedCourse ||
      !selectedSubjects ||
      selectedSubjects.length === 0 ||
      !comments.trim()
    ) {
      let message = "Please fill all required fields before saving.";
      if (!studentGroupName.trim())
        message = "Please enter the Student Group Name.";
      else if (!studentGroupDesc.trim())
        message = "Please enter the Student Group Description.";
      else if (!selectedCoordinator)
        message = "Please select a Group Coordinator.";
      else if (!selectedSession) message = "Please select a Session.";
      else if (!selectedCourse) message = "Please select a Course.";
      else if (!selectedSubjects || selectedSubjects.length === 0)
        message = "Please select at least one Subject.";
      else if (!comments.trim()) message = "Please enter Comments.";

      setIsMessageBoxVisible(true);
      setMessageBox(
        <div className="Container-messageBOX">
          <div className="messagebox-insider1">
            <div className="loader-tc"></div>
            <span>{message}</span>
          </div>
        </div>
      );
      setActiveImg(ErrorImg);
      return;
    }

    if (!selectedStudents || selectedStudents.length === 0) {
      setIsMessageBoxVisible(true);
      setMessageBox(
        <div className="Container-messageBOX">
          <div className="messagebox-insider1">
            <div className="loader-tc"></div>
            <span>Please select at least one student before saving.</span>
          </div>
        </div>
      );
      setActiveImg(ErrorImg);
      return;
    }
    setIsSaving(true);

    const clientBranchNum = clientBranchId ? Number(clientBranchId) : null;
    const normalizedSelectedBranches = Array.isArray(selectedBranches)
      ? selectedBranches.map((id) => Number(id))
      : [];
    const branchSet = new Set(normalizedSelectedBranches.filter(Boolean));
    if (clientBranchNum !== null) branchSet.add(clientBranchNum);
    const branchIdsPayload = Array.from(branchSet);

    const payload = {
      student_ids: (selectedStudents || []).map((id) => Number(id)),
      branch_ids: branchIdsPayload,
      subject_ids: (selectedSubjects || []).map((id) => Number(id)),
    };

    const usernameParam = encodeURIComponent(userName || "");
    const commentsParam = encodeURIComponent(
      comments && comments.trim().length ? comments.trim() : "NA"
    );
    const student_group_name = encodeURIComponent(studentGroupName);
    const student_group_desc = encodeURIComponent(studentGroupDesc);
    const coordParam = Number(selectedCoordinator);

    try {
      let response;
      let data;

      if (studentGroupId) {
        const url =
          `${FULL_API_BASE_URL}/update-student-group` +
          `?student_group_id=${encodeURIComponent(studentGroupId)}` +
          `&username=${usernameParam}` +
          `&student_group_name=${student_group_name}` +
          `&student_group_desc=${student_group_desc}` +
          `&comments=${commentsParam}` +
          `&student_group_coord_emp_id=${coordParam}`;

        response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        });

        data = await response.json();
      } else {
        let url =
          `${FULL_API_BASE_URL}/create-student-group` +
          `?student_group_name=${student_group_name}` +
          `&student_group_desc=${student_group_desc}` +
          `&comments=${commentsParam}` +
          `&course_id=${encodeURIComponent(selectedCourse)}` +
          `&session_id=${encodeURIComponent(selectedSession)}` +
          `&student_group_coord_emp_id=${coordParam}` +
          `&client_branch_id=${encodeURIComponent(clientBranchId)}` +
          `&username=${usernameParam}`;

        response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        });

        data = await response.json();
      }

      if (response.ok && data.status === 200) {
        setIsMessageBoxVisible(true);
        setMessageBox(
          <div className="Container-messageBOX">
            <div className="messagebox-insider1">
              <div className="loader-tc"></div>
              <span>
                {data.message ||
                  (studentGroupId
                    ? "Student group updated successfully!"
                    : "Student group created successfully!")}
              </span>
            </div>
          </div>
        );
        setActiveImg(InterogativeImg);
        if (data.data) {
          setStudentGroupData((prev) => ({ ...(prev || {}), ...data.data }));
          setCurrentStatus(data.data.status ?? currentStatus);
          if (!isCurrentlyActive(data.data.status)) setIsEditMode(false);
        }
        // navigate("/student_group_dashboard");
      } else {
        setIsMessageBoxVisible(true);
        setMessageBox(
          <div className="Container-messageBOX">
            <div className="messagebox-insider1">
              <div className="loader-tc"></div>
              <span>
                {(data && data.message) ||
                  "Failed to save student group. Please try again."}
              </span>
            </div>
          </div>
        );
        setActiveImg(ErrorImg);
      }
    } catch (error) {
      console.error("Error saving/updating student group:", error);
      setIsMessageBoxVisible(true);
      setMessageBox(
        <div className="Container-messageBOX">
          <div className="messagebox-insider1">
            <div className="loader-tc"></div>
            <span>
              An unexpected error occurred while saving the student group.
              Please try again.
            </span>
          </div>
        </div>
      );
      setActiveImg(ErrorImg);
    } finally {
      setIsSaving(false);
    }
  };

  // status modal & toggle
  const [isProcessingStatus, setIsProcessingStatus] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [modalComment, setModalComment] = useState("");

  const handleStatusToggle = () => {
    if (!studentGroupId) {
      setIsMessageBoxVisible(true);
      setMessageBox(
        <div className="Container-messageBOX">
          <div className="messagebox-insider1">
            <div className="loader-tc"></div>
            <span>Student Group ID is missing.</span>
          </div>
        </div>
      );
      setActiveImg(ErrorImg);
      return;
    }

    const currentlyActive = isCurrentlyActive(currentStatus);
    const newStatusNumeric = currentlyActive ? 0 : 1;

    setModalComment("");
    setPendingStatusChange(newStatusNumeric);
    setShowCommentModal(true);
  };

  const handleStatusChangeWithComment = async (comment) => {
    // If no comment provided -> close modal and show error message box, do NOT proceed.
    if (!comment || !comment.toString().trim()) {
      setShowCommentModal(false);
      setIsMessageBoxVisible(true);
      setMessageBox(
        <div className="Container-messageBOX">
          <div className="messagebox-insider1">
            <div className="loader-tc"></div>
            <span>Please enter a comment before changing status.</span>
          </div>
        </div>
      );
      setActiveImg(ErrorImg);
      return;
    }

    // Proceed with status change
    setShowCommentModal(false);
    setIsProcessingStatus(true);

    try {
      const url = `${FULL_API_BASE_URL}/deactivate-student-group?student_group_id=${studentGroupId}&username=${encodeURIComponent(
        userName
      )}&status=${pendingStatusChange}&comments=${encodeURIComponent(comment)}`;

      const resp = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const resJson = await resp.json();
      if (resp.ok && resJson.status === 200) {
        const newStatusLabel =
          resJson.data?.status ??
          (pendingStatusChange === 1 ? "Active" : "Inactive");
        setCurrentStatus(newStatusLabel);
        setStudentGroupData((prev) => ({
          ...(prev || {}),
          status: newStatusLabel,
        }));

        if (!isCurrentlyActive(newStatusLabel)) {
          setIsEditMode(false);
        }

        setIsMessageBoxVisible(true);
        setMessageBox(
          <div className="Container-messageBOX">
            <div className="messagebox-insider1">
              <div className="loader-tc"></div>
              <span>
                {resJson.message || "Student group status updated successfully"}
              </span>
            </div>
          </div>
        );
        setActiveImg(SuccessImg || InterogativeImg);
      } else {
        setIsMessageBoxVisible(true);
        setMessageBox(
          <div className="Container-messageBOX">
            <div className="messagebox-insider1">
              <div className="loader-tc"></div>
              <span>
                {resJson.message || "Failed to update student group status."}
              </span>
            </div>
          </div>
        );
        setActiveImg(ErrorImg);
      }
    } catch (err) {
      console.error("Error toggling student group status:", err);
      setIsMessageBoxVisible(true);
      setMessageBox(
        <div className="Container-messageBOX">
          <div className="messagebox-insider1">
            <div className="loader-tc"></div>
            <span>
              An unexpected error occurred while updating status. Please try
              again.
            </span>
          </div>
        </div>
      );
      setActiveImg(ErrorImg);
    } finally {
      setIsProcessingStatus(false);
      setPendingStatusChange(null);
    }
  };

  const activeFlag = isCurrentlyActive(currentStatus);
  // console.log("Current Status:", currentStatus, "Active Flag:", activeFlag);

  return (
    <div className="create-role-container">
      <div className="create-role-container-ques-paper-del">
        <span className="create-role-heading">Student Group</span>
        <div
          className="cr-header-btn-container"
          style={{ width: "100%", display: "flex", gap: 8 }}
        >
          <button
            className="create-test-section-btn"
            onClick={() => navigate("/student_group_dashboard")}
            style={{ width: "140px" }}
          >
            CANCEL
          </button>
          <button
            className="create-test-section-btn"
            onClick={handleStatusToggle}
            disabled={isProcessingStatus && activeFlag}
            style={{
              cursor:
                isProcessingStatus && activeFlag ? "not-allowed" : "pointer",
              opacity: isProcessingStatus && activeFlag ? 0.6 : 1,
              width: "140px",
            }}
          >
            {activeFlag ? "DEACTIVATE" : "ACTIVATE"}
          </button>
          {!isEditMode ? (
            <button
              className="create-test-section-btn"
              style={{
                marginRight: "0px",
                width: "140px",
                cursor: activeFlag ? "pointer" : "not-allowed",
                backgroundColor: activeFlag ? "" : "#e0e0e0",
              }}
              onClick={handleEditClick}
              disabled={!activeFlag}
            >
              EDIT
            </button>
          ) : (
            <button
              className="create-test-section-btn"
              style={{
                marginRight: "0px",
                width: "140px",
                opacity: isSaving ? 0.6 : 1,
                cursor: isSaving ? "not-allowed" : "pointer",
              }}
              onClick={handleSaveClick}
              disabled={isSaving}
            >
              SAVE
            </button>
          )}
        </div>
      </div>

      <div className="ques-paper-del">
        <div className="personal-header">
          <div className="header">
            <label>USER DETAILS</label>
          </div>
        </div>

        <div className="accordion-content">
          <div className="select-assign-test-container-ques-pap-del">
            <div className="grid-container-ques-paper-del">
              <div className="item1">
                <FormInputs
                  label="Student Group Name"
                  type="text"
                  required
                  value={studentGroupName}
                  onChange={(e) => setStudentGroupName(e.target.value)}
                  disabled={!isEditMode || !activeFlag}
                  disable={!isEditMode || !activeFlag}
                />
              </div>

              <div className="item2">
                <FormInputs
                  label="Student Group Description"
                  type="text"
                  required
                  value={studentGroupDesc}
                  onChange={(e) => setStudentGroupDesc(e.target.value)}
                  disabled={!isEditMode || !activeFlag}
                  disable={!isEditMode || !activeFlag}
                />
              </div>

              <div className="item2">
                <SingleSelectSearchDropdown
                  label="Group Coordinator"
                  width="100%"
                  singleSelect={true}
                  selected={selectedCoordinator}
                  setSelected={(value) => {
                    setSelectedCoordinator(value);
                    // Update the option object when selection changes
                    if (value) {
                      const option = facultyOptions.find((f) => f.id === value);
                      if (option) {
                        setSelectedCoordinatorOption(option);
                      }
                    } else {
                      setSelectedCoordinatorOption(null);
                    }
                  }}
                  options={facultyOptions}
                  placeholder="Search and select Coordinator..."
                  onSearchInputChange={fetchFaculty}
                  isRequired={true}
                  selectedOptionProp={selectedCoordinatorOption} // Use the new state
                  disabled={!isEditMode || !activeFlag}
                />
              </div>

              <div className="item3">
                <SelectDropdownValues
                  label="Session"
                  selected={selectedSession}
                  setSelected={setSelectedSession}
                  options={
                    Array.isArray(sessionsData)
                      ? sessionsData.map((session) => ({
                          label: session.session_name,
                          id: session.session_id,
                        }))
                      : []
                  }
                  isRequired={true}
                  disabled={!isEditMode || !activeFlag}
                  isDisabled={!isEditMode || !activeFlag}
                />
              </div>

              <div className="item4">
                <SelectDropdownValues
                  label="Course"
                  selected={selectedCourse}
                  setSelected={(val) => setSelectedCourse(val)}
                  options={
                    Array.isArray(coursesdata)
                      ? coursesdata.map((course) => ({
                          label: course.course_name,
                          id: course.course_id,
                        }))
                      : []
                  }
                  isRequired={true}
                  disabled={true}
                />
              </div>

              <div className="item5">
                <MultiSeledDropdown
                  label="Subjects"
                  width="100%"
                  selected={selectedSubjects}
                  setSelected={setSelectedSubjects}
                  options={subjects.map((subject) => ({
                    label: subject.subject_name,
                    id: subject.subject_id,
                  }))}
                  showSelectAll={true}
                  isRequired={true}
                  disabled={!isEditMode || !activeFlag}
                />
              </div>

              <div className="item6">
                <MultiSeledDropdown
                  label="Extend To Branch"
                  width="100%"
                  selected={selectedBranches}
                  setSelected={setSelectedBranches}
                  options={branchOptions}
                  showSelectAll={true}
                  disabled={!isEditMode || !activeFlag}
                  placeholder="Select branch to extend this group"
                />
              </div>

              <div className="item7-student-group-details-comments">
                <FormInputs
                  label="Comments"
                  type="text"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  disabled={!isEditMode || !activeFlag}
                  disable={!isEditMode || !activeFlag}
                />
              </div>
            </div>
          </div>

          {showCommentModal && (
            <div
              className="modal-overlay"
              onClick={() => setShowCommentModal(false)}
            >
              <button
                className="modal-close-outside"
                aria-label="Close"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCommentModal(false);
                }}
                style={{
                  position: "absolute",
                  top: "calc(65% - 300px - 8px)",
                  left: "calc(40% + 400px - 18px)",
                }}
              >
                ×
              </button>
              <div
                className="modal-content-all-question"
                onClick={(e) => e.stopPropagation()}
                tabIndex={0}
                style={{
                  outline: "none",
                  width: "500px",
                  maxHeight: "300px",
                  padding: "20px",
                }}
              >
                <div className="modal-header-del">
                  <p
                    style={{
                      fontSize: "18px",
                      color: "#424342",
                      margin: 0,
                      padding: "10px 14px",
                      userSelect: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {`REASON FOR ${activeFlag ? "DEACTIVATION" : "ACTIVATION"}`}
                  </p>
                </div>

                <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                  <textarea
                    value={modalComment}
                    onChange={(e) => setModalComment(e.target.value)}
                    placeholder="Enter your comment..."
                    style={{
                      width: "100%",
                      height: "120px",
                      padding: "10px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      resize: "vertical",
                      fontFamily: "inherit",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div
                  className="modal-actions"
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                  }}
                >
                  <button
                    className="testreportcard-cancel-btn"
                    onClick={() => {
                      setModalComment("");
                      setShowCommentModal(false);
                    }}
                  >
                    CANCEL
                  </button>
                  <button
                    className="testreportcard-create-btn"
                    onClick={() => handleStatusChangeWithComment(modalComment)}
                  >
                    SAVE
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="ques-paper-del" style={{ marginTop: "20px" }}>
        <div className="personal-header">
          <div className="header">
            <label>ADD STUDENTS</label>
          </div>
          <div className="accordion-btn" onClick={toggleAccordionShareBranch}>
            {isOpenShareBranch ? (
              <div className="minus"></div>
            ) : (
              <div className="plus">+</div>
            )}
          </div>
        </div>
        {isOpenShareBranch && (
          <div className="accordion-content">
            <div
              className="select-assign-test-container-ques-pap-del"
              style={{ padding: "20px 0px 20px 0px" }}
            >
              <div className="grid-container-ques-paper-del">
                <div className="item1" style={{ marginLeft: "20px" }}>
                  <DropdownWithPagination
                    label="Add Students"
                    width="100%"
                    selected={selectedStudents}
                    setSelected={setSelectedStudents}
                    onSearchInputChange={fetchStudents}
                    options={studentOptions}
                    isRequired={false}
                    showSelectAll={false}
                    itemsPerPage={4}
                    isDisabled={!isEditMode || !activeFlag}
                  />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <TableWithCrossStudentGroup
                    headers={headers}
                    data={tableData}
                    searchPlaceholder="Search students..."
                    selectedRows={selectedStudents}
                    setSelectedRows={handleTableRowSelection}
                    renderQuestionText={(text) => text}
                    renderQuestionTextModel={(text) => text}
                    expandedStates={{}}
                    toggleImage={() => {}}
                    pageType="userEdit"
                    onRowLinkClick={(row) => console.log("Row clicked:", row)}
                    filterConfig={{}}
                    handleDeleteRow={handleDeleteRow}
                    isDisabled={!isEditMode || !activeFlag}
                    isEditMode={isEditMode}
                    hideIdColumn={true}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentGroupEdit;
